import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { Check, LogOut, Play, X } from 'lucide-react'
import Loader from '../components/Loader'
import { GameUploadCard, QuickAuthButtons } from '../components/landing/GameUploadCard'
import { ProductPreview } from '../components/landing/ProductPreview'
import { PreviewLauncher } from '../components/landing/PreviewLauncher'
import supabase from '../supabaseClient'
import { COMMUNITY_SPORT_LABELS } from '../lib/communitySport'
import { ALL_COUNTRIES } from '../components/signup/countries'
import { getPackagesForCommunity, getPublicGamePackages, withTestBundle } from '../lib/communityCreditPricing'
import { getYoutubeLinkValidationReason } from '../components/academy-schedule/academySchedule.helpers'
import { uploadToS3 } from '../lib/awsS3UploadService'
import {
  clearQuickUploadIntent,
  clearQuickUploadOpen,
  createQuickUploadGame,
  patchQuickUploadIntent,
  readQuickUploadIntent,
  readQuickUploadUrl,
  rememberQuickUploadUrl,
  writeQuickUploadIntent,
} from '../lib/quickUpload'
import { signOutClientSession } from '../utils/authSignOut'
import {
  USERS_LOGIN_SELECT,
  profileCanAccessPortal,
  routePortalAfterAuth,
} from '../utils/signupFunnelRedirect'
import { readLastPortal } from '../utils/lastPortal'
import { fetchCommunityPlatform, hubGamePath, isBasicCommunity, markHubCreditsReturn } from '../lib/hub'

function formatPlanPrice(value) {
  const amount = Number(value)
  return Number.isInteger(amount) ? String(amount) : amount.toFixed(2)
}

const COUNTRIES_ALPHA = [...ALL_COUNTRIES].sort((a, b) => a[0].localeCompare(b[0], 'en'))

const DEFAULT_COUNTRY_ISO2 = 'US'

const PLAN_INCLUDES = [
  'Individual stats and highlight clips for each player',
  'Game highlights of all the best moments of the game',
  'A shareable game page where players find themselves and claim their stats',
]

/** Keep in sync with the `/sports/*` stat lists in content/seoPages.mjs. */
const SPORT_STATS = {
  Football: ['Goals', 'Assists', 'Shots on target', 'Shots off target', 'Chances created', 'Dribbles', 'Tackles', 'Saves'],
  Basketball: ['Points', 'Threes', 'Free throws', 'Field goals', 'Rebounds', 'Assists', 'Blocks', 'Turnovers'],
  Padel: ['Winners', 'Aces', 'Smashes', 'Lobs', 'Drop shots', 'Long rallies', 'Double faults', 'Unforced errors'],
}

const SPORT_HIGHLIGHTS = [
  'Game highlights of the best moments',
  'A personal highlight reel for every player',
]

function SportPreview({ sport }) {
  const reduceMotion = useReducedMotion()
  const stats = SPORT_STATS[sport] || SPORT_STATS.Football
  const fade = reduceMotion
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } }
    : { initial: { opacity: 0, y: 6 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -4 } }

  return (
    <section aria-live="polite" className="rounded-2xl border border-[#e7e5e4] bg-white px-4 py-3.5">
      <AnimatePresence mode="wait" initial={false}>
        <motion.div key={sport} {...fade} transition={{ duration: 0.2, ease: [0.25, 1, 0.5, 1] }}>
          <p className="text-sm font-semibold">You&apos;ll get stats for every player</p>
          <ul className="mt-2.5 flex flex-wrap gap-1.5">
            {stats.map((stat) => (
              <li
                key={stat}
                className="rounded-full bg-[#f5f5f4] px-2.5 py-1 text-xs font-medium text-[#44403c]"
              >
                {stat}
              </li>
            ))}
          </ul>
          <ul className="mt-3 space-y-1.5 border-t border-[#f0efed] pt-3">
            {SPORT_HIGHLIGHTS.map((item) => (
              <li key={item} className="flex items-center gap-2.5 text-sm text-[#44403c]">
                <span
                  aria-hidden
                  className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-[#0c3a42]/10 text-[#0c3a42]"
                >
                  <Play className="ml-px h-2.5 w-2.5 fill-current" strokeWidth={0} />
                </span>
                {item}
              </li>
            ))}
          </ul>
        </motion.div>
      </AnimatePresence>
    </section>
  )
}

function profileName(user) {
  return (
    user?.user_metadata?.full_name ||
    user?.user_metadata?.name ||
    String(user?.email || '').split('@')[0] ||
    ''
  )
}

function StepBack({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex min-h-11 items-center gap-1 rounded-sm text-sm font-semibold text-[#57534e] hover:text-[#1c1917] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1c1917]"
    >
      <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" aria-hidden="true">
        <path d="M12.5 4.5 7 10l5.5 5.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      Back
    </button>
  )
}

const FLOW_STEPS = [
  { id: 'footage', label: 'Game' },
  { id: 'details', label: 'Team' },
  { id: 'account', label: 'Sign in' },
  { id: 'plan', label: 'Payment' },
]

const HUB_FLOW_STEPS = [
  { id: 'footage', label: 'Game' },
  { id: 'plan', label: 'Confirm' },
]

function FlowProgress({ step, steps = FLOW_STEPS }) {
  const current = Math.max(0, steps.findIndex((item) => item.id === step))
  const fill = steps.length > 1 ? (current / (steps.length - 1)) * 100 : 0
  const inset = `${50 / steps.length}%`
  return (
    <div className="relative mb-4 sm:mb-8">
      <div className="absolute top-3 h-px bg-[#e7e5e4]" style={{ left: inset, right: inset }} aria-hidden="true">
        <span className="block h-full bg-[#071018] transition-[width] duration-300" style={{ width: `${fill}%` }} />
      </div>
      <ol className="relative grid" style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }} aria-label="Progress">
        {steps.map((item, index) => {
          const active = index === current
          const done = index < current
          return (
            <li key={item.id} className="flex flex-col items-center" aria-current={active ? 'step' : undefined}>
              <span
                className={`relative z-10 flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-semibold ${
                  done || active
                    ? 'bg-[#071018] text-white'
                    : 'border border-[#d6d3d1] bg-[#F4F6F8] text-[#78716c]'
                }`}
              >
                {done ? (
                  <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" aria-hidden="true">
                    <path d="M3.5 8.2 6.4 11l6.1-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : (
                  index + 1
                )}
              </span>
              <span className={`mt-2 text-center text-xs leading-none ${active ? 'font-semibold text-[#1c1917]' : done ? 'font-medium text-[#44403c]' : 'font-medium text-[#78716c]'}`}>
                {item.label}
              </span>
            </li>
          )
        })}
      </ol>
    </div>
  )
}

/** Keeps the background upload visible on every step after the game is added. */
function UploadStatus({ status, progress, fileName }) {
  if (status === 'uploading') {
    return (
      <div className="mb-4 rounded-2xl border border-[#e7e5e4] bg-white px-4 py-2.5 sm:mb-6 sm:py-3" role="status" aria-live="polite">
        <div className="flex items-baseline justify-between gap-3 text-sm">
          <span className="min-w-0 truncate font-medium text-[#1c1917]">Uploading {fileName || 'your game'}</span>
          <span className="shrink-0 font-semibold tabular-nums text-[#0c3a42]">{progress}%</span>
        </div>
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[#e7e5e4]">
          <div
            className="h-full rounded-full bg-[#0c3a42] transition-[width] duration-300"
            style={{ width: `${Math.max(4, Math.min(100, progress))}%` }}
          />
        </div>
        <p className="mt-2 text-xs text-[#57534e]">Keep this tab open. You can carry on meanwhile.</p>
      </div>
    )
  }
  return null
}

export default function QuickUpload({ onClose, inHub = false, buyOnly = false }) {
  const navigate = useNavigate()
  const location = useLocation()
  const [sessionUser, setSessionUser] = useState(undefined)
  const [team, setTeam] = useState('')
  const [sport, setSport] = useState('Football')
  const [country, setCountry] = useState(DEFAULT_COUNTRY_ISO2)
  const [savedFileName, setSavedFileName] = useState('')
  const [source, setSource] = useState('video')
  const [youtubeUrl, setYoutubeUrl] = useState(() => readQuickUploadUrl())
  const [file, setFile] = useState(null)
  const [uploadStatus, setUploadStatus] = useState('idle')
  const [uploadProgress, setUploadProgress] = useState(0)
  const [videoUrl, setVideoUrl] = useState('')
  const [packageId, setPackageId] = useState('game_1')
  const asideRef = useRef(null)
  const [previewFrameMax, setPreviewFrameMax] = useState(undefined)
  const [submitting, setSubmitting] = useState(false)
  const [hubTeam, setHubTeam] = useState(null)
  const planPackages = withTestBundle(
    hubTeam ? getPackagesForCommunity(hubTeam.id) : getPublicGamePackages(),
    true,
  )
  const [leaving, setLeaving] = useState(false)
  const [error, setError] = useState('')
  const [authError] = useState(location.state?.oauthError || '')
  const [accountChecked, setAccountChecked] = useState(false)
  const [step, setStep] = useState(buyOnly ? 'plan' : 'footage')
  const sportRef = useRef(sport)
  const packageIdRef = useRef(packageId)
  const sessionIdRef = useRef(null)
  sportRef.current = sport
  packageIdRef.current = packageId
  const sessionId = sessionUser === undefined ? undefined : sessionUser?.id || null
  sessionIdRef.current = sessionId || null

  const uploading = source === 'video' && uploadStatus === 'uploading'
  const hubHasGames = Boolean(hubTeam) && hubTeam.credits > 0 && !buyOnly

  const requestClose = () => {
    if (!onClose) return
    if (uploading && !window.confirm('Your game is still uploading. Close and stop the upload?')) return
    onClose()
  }

  const leaveToLanding = async () => {
    if (leaving) return
    if (uploading && !window.confirm('Your game is still uploading. Log out and stop the upload?')) return
    setLeaving(true)
    clearQuickUploadIntent()
    clearQuickUploadOpen()
    onClose?.()
    try {
      await signOutClientSession()
    } catch (err) {
      console.error('[QuickUpload] sign out error:', err)
    }
    if (!onClose) navigate('/', { replace: true })
  }

  useEffect(() => {
    let cancelled = false
    supabase.auth.getSession().then(({ data }) => {
      if (!cancelled) setSessionUser(data.session?.user || null)
    })
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSessionUser(session?.user || null)
    })
    return () => {
      cancelled = true
      subscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (buyOnly) return
    const intent = readQuickUploadIntent()
    if (!intent || intent.stage === 'ready') return
    if (intent.teamName) setTeam(intent.teamName)
    if (intent.sport) setSport(intent.sport)
    if (intent.countryIso2) setCountry(intent.countryIso2)
    if (intent.source === 'youtube' || intent.source === 'video') setSource(intent.source)
    if (intent.youtubeUrl) setYoutubeUrl(intent.youtubeUrl)
    if (intent.videoUrl) {
      setVideoUrl(intent.videoUrl)
      setUploadStatus('done')
      setUploadProgress(100)
    }
    if (intent.fileName) setSavedFileName(intent.fileName)
    if (intent.packageId) setPackageId(intent.packageId)
    const hasFootage = Boolean(intent.videoUrl) || (intent.source === 'youtube' && Boolean(intent.youtubeUrl))
    if (inHub) {
      if (intent.stage === 'pay' && hasFootage) setStep('plan')
      return
    }
    if ((intent.stage === 'account' || intent.stage === 'pay') && hasFootage && intent.teamName) setStep('account')
    else if (hasFootage) setStep('details')
  }, [])

  useEffect(() => {
    if (step === 'account' && sessionUser) setStep('plan')
  }, [step, sessionUser])

  useEffect(() => {
    if (hubTeam && (step === 'details' || step === 'account')) setStep('plan')
  }, [step, hubTeam])

  useEffect(() => {
    if (!onClose) return undefined
    const onKey = (event) => {
      if (event.key === 'Escape') requestClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  })

  useEffect(() => {
    if (!uploading) return undefined
    const warn = (event) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [uploading])

  useEffect(() => {
    const aside = asideRef.current
    const dialog = aside?.closest('[role="dialog"]')
    const main = aside?.closest('main')
    if (!aside || !dialog || !main) return undefined
    const fit = () => {
      const figure = aside.querySelector('figure')
      const frame = figure?.children[1]
      if (!frame || !aside.offsetParent) return
      const offset = aside.getBoundingClientRect().top - dialog.getBoundingClientRect().top + main.scrollTop
      const labelAndCaption = figure.offsetHeight - frame.offsetHeight
      const dialogMax = Math.min(920, window.innerHeight - 32)
      const mainBottomPadding = parseFloat(getComputedStyle(main).paddingBottom) || 0
      setPreviewFrameMax(Math.max(360, Math.floor(dialogMax - offset - mainBottomPadding - labelAndCaption - 8)))
    }
    fit()
    window.addEventListener('resize', fit)
    return () => window.removeEventListener('resize', fit)
  }, [step, accountChecked])

  useEffect(() => {
    if (sessionId === undefined) return undefined
    if (!sessionId) {
      setHubTeam(null)
      setAccountChecked(true)
      return undefined
    }

    let cancelled = false
    setAccountChecked(false)
    ;(async () => {
      const { data: profile, error: profileError } = await supabase
        .from('Users')
        .select(`${USERS_LOGIN_SELECT}, super_admin`)
        .eq('user_id', sessionId)
        .maybeSingle()

      if (cancelled) return

      if (!profileError && profile) {
        const superAdmin = ['true', 'True'].includes(String(profile.super_admin))
        if (profile.admin_Pass && !superAdmin) {
          navigate('/admin/video-analysis', { replace: true })
          return
        }

        if (profileCanAccessPortal(profile, 'community')) {
          const hub = await fetchCommunityPlatform(profile.community_id)
          if (cancelled) return
          if (isBasicCommunity(hub)) {
            setHubTeam({ id: profile.community_id, name: hub.name || '', credits: Number(hub.credits) || 0 })
            setAccountChecked(true)
            return
          }
        }

        if (superAdmin) {
          setAccountChecked(true)
          return
        }

        const lastPortal = readLastPortal()
        const portals = []
        if (lastPortal && profileCanAccessPortal(profile, lastPortal)) portals.push(lastPortal)
        for (const portal of ['community', 'academy', 'venue']) {
          if (portals.includes(portal)) continue
          if (profileCanAccessPortal(profile, portal)) portals.push(portal)
        }

        if (portals[0]) {
          if (portals[0] === 'community') {
            const intent = readQuickUploadIntent()
            const url = readQuickUploadUrl() || (intent?.source === 'youtube' ? intent.youtubeUrl : '')
            if (intent?.source === 'video' && intent.videoUrl) {
              writeQuickUploadIntent({ ...intent, stage: 'ready' })
            } else if (url && !getYoutubeLinkValidationReason(url)) {
              writeQuickUploadIntent({
                ...(intent || {}),
                stage: 'ready',
                source: 'youtube',
                youtubeUrl: url,
                sport: intent?.sport || sportRef.current,
                packageId: intent?.packageId || packageIdRef.current,
              })
            }
          }
          const handled = await routePortalAfterAuth(navigate, profile, portals[0], {
            userId: sessionId,
          })
          if (handled || cancelled) return
        }
      }

      if (!cancelled) setAccountChecked(true)
    })()

    return () => {
      cancelled = true
    }
  }, [sessionId, navigate])

  const handleYoutube = (value) => {
    setYoutubeUrl(value)
    rememberQuickUploadUrl(value)
  }

  const handleFile = (next) => {
    setVideoUrl('')
    setSavedFileName(next?.name || '')
    setUploadProgress(0)
    setUploadStatus(next ? 'uploading' : 'idle')
    setError('')
    setFile(next)
    if (!next) patchQuickUploadIntent({ videoUrl: '', fileName: '' })
  }

  useEffect(() => {
    if (source !== 'video' || !file) return undefined
    const controller = new AbortController()
    let cancelled = false
    const ownerId = sessionIdRef.current
    setUploadStatus('uploading')
    setUploadProgress(0)
    setVideoUrl('')
    uploadToS3(
      file,
      ownerId || 'quick-upload',
      (percent) => {
        if (!cancelled) setUploadProgress(Math.max(0, Math.min(100, Math.round(percent || 0))))
      },
      !ownerId,
      { signal: controller.signal, skipCompress: true },
    )
      .then((result) => {
        if (cancelled) return
        const url = result?.videoUrl || result?.publicUrl || ''
        if (!url) throw new Error('The video uploaded, but no link came back.')
        setVideoUrl(url)
        setSavedFileName(file.name)
        setUploadProgress(100)
        setUploadStatus('done')
        const current = readQuickUploadIntent()
        patchQuickUploadIntent({
          stage: current?.stage || 'draft',
          source: 'video',
          videoUrl: url,
          fileName: file.name,
        })
      })
      .catch((err) => {
        if (cancelled || controller.signal.aborted) return
        const message = err?.message || 'The upload failed. Choose the video again.'
        if (/abort/i.test(message)) return
        setUploadStatus('error')
        setError(`${message} Choose the video again.`)
      })
    return () => {
      cancelled = true
      controller.abort()
    }
  }, [file, source])

  const linkReady = Boolean(String(youtubeUrl || '').trim()) && !getYoutubeLinkValidationReason(youtubeUrl)
  const footageAdded = source === 'youtube'
    ? linkReady
    : uploadStatus === 'uploading' || (uploadStatus === 'done' && Boolean(videoUrl))
  const footageReady = source === 'youtube' ? linkReady : uploadStatus === 'done' && Boolean(videoUrl)

  const footageIntent = () => ({
    source,
    youtubeUrl: source === 'youtube' ? youtubeUrl.trim() : '',
    videoUrl: source === 'video' ? videoUrl : '',
    fileName: source === 'video' ? file?.name || savedFileName : '',
    sport,
    teamName: hubTeam?.name || team.trim(),
    countryIso2: country,
    packageId,
  })

  const continueFromFootage = (event) => {
    event.preventDefault()
    setError('')
    if (source === 'youtube') {
      const reason = getYoutubeLinkValidationReason(youtubeUrl)
      if (reason) {
        setError(reason)
        return
      }
    } else if (!footageAdded) {
      setError(uploadStatus === 'error' ? 'The upload failed. Choose the video again.' : 'Choose the game video first.')
      return
    }
    if (hubHasGames) {
      spendGameCredit()
      return
    }
    patchQuickUploadIntent({ stage: 'draft', ...footageIntent() })
    setStep(hubTeam ? 'plan' : 'details')
  }

  const continueFromDetails = (event) => {
    event.preventDefault()
    setError('')
    if (!team.trim()) {
      setError('Add your team or group name.')
      return
    }
    if (!country) {
      setError('Choose a country.')
      return
    }
    patchQuickUploadIntent({ stage: 'draft', ...footageIntent() })
    setStep(sessionUser ? 'plan' : 'account')
  }

  const spendGameCredit = async () => {
    if (submitting || !hubTeam) return
    setError('')
    if (!footageReady) {
      setError(uploading ? 'Wait until the video has uploaded.' : 'The video is missing. Go back and add it again.')
      return
    }
    setSubmitting(true)
    const created = await createQuickUploadGame({
      communityId: hubTeam.id,
      communityName: hubTeam.name,
      intent: { ...footageIntent(), fullName: profileName(sessionUser).trim() },
    })
    if (!created.ok) {
      setSubmitting(false)
      setError(created.error || 'Could not create the game.')
      return
    }
    clearQuickUploadIntent()
    clearQuickUploadOpen()
    onClose?.()
    navigate(hubGamePath(created.matchId))
  }

  const saveBeforeAuth = () => {
    writeQuickUploadIntent({ ...(readQuickUploadIntent() || {}), stage: 'account', ...footageIntent() })
  }

  const goBackStep = () => {
    setError('')
    if (step === 'details') setStep('footage')
    else if (step === 'account') setStep('details')
    else if (step === 'plan') setStep(hubTeam ? 'footage' : 'details')
  }

  const pay = (event) => {
    event.preventDefault()
    if (submitting || step !== 'plan') return
    setError('')
    if (inHub) markHubCreditsReturn()
    if (buyOnly) {
      const selected = planPackages.find((pack) => pack.id === packageId) || planPackages[0]
      setSubmitting(true)
      clearQuickUploadIntent()
      navigate(`/upload/pay?pack=${encodeURIComponent(selected.id)}`)
      return
    }
    if (!footageReady) {
      setError(uploading ? 'Wait until the video has uploaded.' : 'The video is missing. Go back and add it again.')
      return
    }

    const selected = planPackages.find((pack) => pack.id === packageId) || planPackages[0]
    setSubmitting(true)
    writeQuickUploadIntent({
      stage: 'pay',
      ...footageIntent(),
      packageId: selected.id,
      fullName: profileName(sessionUser).trim(),
      logoUrl: '',
    })
    navigate(`/upload/pay?pack=${encodeURIComponent(selected.id)}`)
  }

  if (sessionUser === undefined || (sessionUser && !accountChecked)) {
    return (
      <div className={onClose ? 'flex flex-1 items-center justify-center bg-[#F4F6F8] py-16' : 'flex min-h-[100svh] items-center justify-center bg-[#07080c]'}>
        <Loader size={32} />
      </div>
    )
  }

  const selectedPack = planPackages.find((pack) => pack.id === packageId) || planPackages[0]
  const fieldClass =
    'h-12 w-full rounded-2xl border border-[#e7e5e4] bg-white px-3 text-base text-[#1c1917] placeholder:text-[#78716c] focus:border-[#071018] focus:outline-none focus:ring-2 focus:ring-[#071018]/15'
  const primaryButtonClass =
    'flex min-h-[52px] w-full items-center justify-center rounded-full text-base font-bold tracking-[-0.02em] text-[#071018] shadow-[0_14px_32px_-12px_rgba(113,205,217,0.85),inset_0_1px_0_rgba(255,255,255,0.45)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8fe0ec] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'
  const primaryButtonStyle = { background: 'linear-gradient(180deg, #8fe0ec 0%, #71cdd9 58%, #5dbac6 100%)' }
  const shownFileName = file?.name || savedFileName
  const errorLine = error ? (
    <p role="alert" className="mt-6 text-sm text-red-700">
      {error}
    </p>
  ) : null

  return (
    <div className={onClose ? 'flex min-h-0 flex-1 flex-col bg-[#F4F6F8] font-jakarta text-[#1c1917]' : 'min-h-[100svh] bg-[#F4F6F8] font-jakarta text-[#1c1917]'}>
      {onClose ? null : (
        <header className="z-50 shrink-0 bg-[#1E2A3C]">
          <div className="flex h-14 w-full items-center justify-start gap-2 px-4 sm:px-5">
            <button
              type="button"
              onClick={leaveToLanding}
              disabled={leaving}
              className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-sm font-semibold text-white hover:bg-white/10 disabled:opacity-60"
            >
              <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" aria-hidden="true">
                <path d="M12.5 4.5 7 10l5.5 5.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Back
            </button>
            <img src="/logo_white.png" alt="Statbox" className="h-3 sm:h-3.5 w-auto" />
          </div>
        </header>
      )}

      <main
        className={
          onClose
            ? 'relative flex min-h-0 flex-1 flex-col overflow-y-auto px-5 pb-2 pt-[max(0.5rem,env(safe-area-inset-top))] sm:pb-8 sm:pt-6 lg:has-[[data-wide-step]]:px-8'
            : 'mx-auto w-full max-w-lg px-5 pb-16 pt-8 lg:has-[[data-wide-step]]:max-w-[1000px]'
        }
      >
        {onClose ? (
          <div className="-mr-2 flex items-center justify-between sm:mb-2">
            {buyOnly ? (
              <span className="-ml-2 px-2 text-base font-bold tracking-tight">Buy games</span>
            ) : step !== 'footage' ? (
              <div className="-ml-2">
                <StepBack onClick={goBackStep} />
              </div>
            ) : sessionUser && !inHub ? (
              <button
                type="button"
                onClick={leaveToLanding}
                disabled={leaving}
                className="-ml-2 inline-flex min-h-11 items-center gap-1.5 rounded-full px-3 text-sm font-semibold text-[#44403c] hover:bg-[#e7e5e4] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1c1917] disabled:opacity-60"
              >
                <LogOut className="h-4 w-4" aria-hidden />
                {leaving ? 'Logging out…' : 'Log out'}
              </button>
            ) : (
              <span className="h-11 w-11 shrink-0" aria-hidden />
            )}
            <button
              type="button"
              onClick={requestClose}
              aria-label="Close"
              className="flex h-11 w-11 items-center justify-center rounded-full text-[#44403c] hover:bg-[#e7e5e4] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1c1917]"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        ) : null}
        <form
          className={step === 'footage' ? 'flex flex-1 flex-col lg:block' : undefined}
          onSubmit={
            step === 'plan' ? pay
              : step === 'details' ? continueFromDetails
                : step === 'footage' ? continueFromFootage
                  : (event) => event.preventDefault()
          }
        >
          {!onClose && step !== 'footage' ? (
            <div className="mb-3">
              <StepBack onClick={goBackStep} />
            </div>
          ) : null}
          {buyOnly || hubHasGames ? null : <FlowProgress step={step} steps={hubTeam ? HUB_FLOW_STEPS : FLOW_STEPS} />}
          {step !== 'footage' && source === 'video' && uploadStatus === 'uploading' ? (
            <UploadStatus status={uploadStatus} progress={uploadProgress} fileName={shownFileName} />
          ) : null}

          {step === 'footage' ? (
          <div
            data-wide-step={inHub ? undefined : ''}
            className={`flex flex-1 flex-col ${inHub ? 'lg:block' : 'lg:grid lg:grid-cols-[minmax(0,1fr)_400px] lg:gap-x-10'}`}
          >
          <div className="flex min-w-0 flex-1 flex-col lg:block">
          <h1 id="quick-upload-title" className="text-[1.625rem] font-bold leading-tight tracking-tight sm:text-[2rem]">Add your game</h1>
          <p className="mt-1.5 max-w-[42ch] text-[15px] leading-relaxed text-[#57534e] sm:mt-2 sm:text-base">
            The full video, or a YouTube or Veo link. It keeps uploading while you do the next steps.
          </p>
          <section className="mt-4 flex flex-1 flex-col sm:mt-6 lg:block">
            <GameUploadCard
              grow
              source={source}
              onSourceChange={(next) => { setError(''); setSource(next) }}
              youtubeUrl={youtubeUrl}
              onYoutubeUrlChange={handleYoutube}
              fileName={shownFileName}
              onFile={handleFile}
              uploadStatus={uploadStatus}
              uploadProgress={uploadProgress}
            />
          </section>
          <fieldset className="mt-3 sm:mt-5">
            <legend className="sr-only sm:not-sr-only sm:mb-2 sm:block sm:text-sm sm:font-medium sm:text-[#1c1917]">Sport</legend>
            <div className="grid grid-cols-3 gap-1 rounded-2xl border border-[#e7e5e4] bg-white p-1" role="radiogroup" aria-label="Sport">
              {COMMUNITY_SPORT_LABELS.map((label) => {
                const selected = sport === label
                return (
                  <button
                    key={label}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => setSport(label)}
                    className={`min-h-11 rounded-xl text-sm font-semibold transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#071018] focus-visible:ring-offset-1 ${
                      selected
                        ? 'bg-[#071018] text-white shadow-[0_4px_10px_-6px_rgba(7,16,24,0.6)]'
                        : 'text-[#57534e] hover:bg-[#f5f5f4] hover:text-[#1c1917]'
                    }`}
                  >
                    {label}
                  </button>
                )
              })}
            </div>
          </fieldset>

          {inHub ? null : (
            <>
              <div className="mt-3 sm:mt-5 lg:hidden">
                <PreviewLauncher sport={sport} />
              </div>

              <div className="mt-5 hidden lg:block lg:[@media(max-height:880px)]:hidden">
                <SportPreview sport={sport} />
              </div>
            </>
          )}

          {errorLine}
          <div className="sticky bottom-0 z-10 -mx-5 mt-1 bg-gradient-to-t from-[#F4F6F8] from-70% to-transparent px-5 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-3 sm:mt-5 sm:pt-4 lg:static lg:mx-0 lg:mt-6 lg:bg-none lg:p-0">
            {hubTeam ? (
              <p className="mb-2 text-center text-[13px] text-[#57534e] sm:text-sm">
                {hubHasGames
                  ? `You have ${hubTeam.credits} ${hubTeam.credits === 1 ? 'game' : 'games'} left.`
                  : 'You have no games left. Pick one game or a pack next.'}
              </p>
            ) : null}
            <button
              type="submit"
              disabled={!footageAdded || submitting || (hubHasGames && uploading)}
              className={primaryButtonClass}
              style={primaryButtonStyle}
            >
              {!hubTeam
                ? 'Continue'
                : hubHasGames
                  ? submitting
                    ? 'Creating the game…'
                    : uploading
                      ? `Finishing upload ${uploadProgress}%`
                      : 'Create game (uses 1 game credit)'
                  : 'Buy & create game'}
            </button>
          </div>
          </div>

          {inHub ? null : (
            <aside ref={asideRef} className="hidden lg:block">
              <div className="sticky top-0 pt-1">
                <ProductPreview sport={sport} maxFrameHeight={previewFrameMax} />
              </div>
            </aside>
          )}
          </div>
          ) : step === 'details' ? (
          <>
          <h1 id="quick-upload-title" className="text-[1.625rem] font-bold leading-tight tracking-tight sm:text-[2rem]">Your team</h1>
          <p className="mt-1.5 max-w-[38ch] text-[15px] leading-relaxed text-[#57534e] sm:mt-2 sm:text-base">
            Where this game, its stats, and its highlights will live.
          </p>
          <section className="mt-5 space-y-4 sm:mt-7">
            <div>
              <label htmlFor="upload-team" className="mb-1.5 block text-sm font-medium">
                Team or group name
              </label>
              <input
                id="upload-team"
                value={team}
                onChange={(event) => setTeam(event.target.value)}
                autoComplete="organization"
                required
                placeholder="e.g. Friday Football"
                className={fieldClass}
              />
            </div>
            <div>
              <label htmlFor="upload-country" className="mb-1.5 block text-sm font-medium">
                Country
              </label>
              <select
                id="upload-country"
                value={country}
                onChange={(event) => setCountry(event.target.value)}
                required
                className={fieldClass}
              >
                <option value="">Choose a country</option>
                {COUNTRIES_ALPHA.map(([label, iso]) => (
                  <option key={iso} value={iso}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </section>

          {errorLine}
          <button
            type="submit"
            className={`mt-6 sm:mt-8 ${primaryButtonClass}`}
            style={primaryButtonStyle}
          >
            Continue
          </button>
          </>
          ) : step === 'account' ? (
          <>
          <h1 id="quick-upload-title" className="text-[2rem] font-bold leading-tight tracking-tight">Sign in to continue</h1>
          <p className="mt-2 max-w-[38ch] text-base leading-relaxed text-[#57534e]">
            Sign in so the game lands in your account. No password.
          </p>
          {uploading ? (
            <p className="mt-6 text-sm leading-relaxed text-[#57534e]">
              Sign-in opens once the upload finishes, so it isn’t cut off.
            </p>
          ) : null}
          <div className="mt-6">
            <QuickAuthButtons disabled={uploading} error={authError} onBeforeAuth={saveBeforeAuth} />
          </div>
          </>
          ) : (
          <>
          {buyOnly ? (
            <p id="quick-upload-title" className="max-w-[38ch] text-[15px] leading-relaxed text-[#57534e] sm:text-base">
              {hubTeam
                ? `You have ${hubTeam.credits} ${hubTeam.credits === 1 ? 'game' : 'games'} left. Each game you upload uses one.`
                : 'Each game you upload uses one.'}
            </p>
          ) : hubTeam ? (
            <p id="quick-upload-title" className="max-w-[38ch] text-[15px] leading-relaxed text-[#57534e] sm:text-base">
              Pay for this game, or take a pack if the team plays every week.
            </p>
          ) : (
            <>
              <h1 id="quick-upload-title" className="text-[1.625rem] font-bold leading-tight tracking-tight sm:text-[2rem]">Choose your plan</h1>
              <p className="mt-1.5 max-w-[38ch] text-[15px] leading-relaxed text-[#57534e] sm:mt-2 sm:text-base">
                Pay for this game, or take a pack if the team plays every week.
              </p>
            </>
          )}

          <section className="mt-4 sm:mt-6">
            <div className="space-y-2" role="radiogroup" aria-label="Choose your plan">
              {planPackages.map((pack) => {
                const selected = packageId === pack.id
                const title = pack.test
                  ? 'Test payment'
                  : pack.credits === 1
                    ? hubTeam ? 'One game' : 'This game'
                    : `${pack.credits}-game pack`
                const detail = pack.test
                  ? 'For testing. A real card payment for 1 game.'
                  : pack.credits === 1
                    ? 'Full game stats and video highlights'
                    : `${pack.credits} games of stats and highlights · $${formatPlanPrice(pack.perGame)}/game`
                return (
                  <button
                    key={pack.id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => setPackageId(pack.id)}
                    className={`flex min-h-[64px] w-full items-center justify-between gap-4 rounded-2xl border-2 bg-white px-4 py-2.5 text-left sm:min-h-[72px] sm:py-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1c1917] ${
                      selected ? 'border-[#1c1917]' : 'border-[#e7e5e4] hover:border-[#d6d3d1]'
                    }`}
                  >
                    <span className="min-w-0">
                      <span className="block text-base font-semibold">{title}</span>
                      <span className="mt-0.5 block text-sm leading-snug text-[#57534e]">{detail}</span>
                      {pack.compareAt && pack.savings ? (
                        <span className="mt-1 block text-xs font-semibold text-[#0c3a42]">Save {pack.savings}% on your first game</span>
                      ) : null}
                    </span>
                    <span className="flex shrink-0 items-center gap-3">
                      <span className="text-right">
                        {pack.compareAt ? (
                          <span className="block text-sm text-[#57534e] line-through tabular-nums">
                            ${Number(pack.compareAt).toFixed(2)}
                          </span>
                        ) : null}
                        <span className="block text-lg font-bold tabular-nums">
                          ${formatPlanPrice(pack.price)}
                        </span>
                      </span>
                      <span
                        aria-hidden
                        className={`flex h-5 w-5 items-center justify-center rounded-full border ${
                          selected ? 'border-[#1c1917]' : 'border-[#d6d3d1]'
                        }`}
                      >
                        {selected ? <span className="h-2.5 w-2.5 rounded-full bg-[#1c1917]" /> : null}
                      </span>
                    </span>
                  </button>
                )
              })}
            </div>
          </section>

          <section aria-labelledby="plan-includes-title" className="mt-4 rounded-2xl bg-white/60 px-4 py-3 sm:mt-6 sm:py-4">
            <h2 id="plan-includes-title" className="text-sm font-semibold">
              What&apos;s included
            </h2>
            <ul className="mt-2 space-y-1.5 sm:mt-3 sm:space-y-2.5">
              {PLAN_INCLUDES.map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-[13px] leading-snug text-[#44403c] sm:text-sm">
                  <span
                    aria-hidden
                    className="mt-px flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-[#0c3a42]/10 text-[#0c3a42]"
                  >
                    <Check className="h-3 w-3" strokeWidth={3} />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </section>

          <div className="sticky bottom-0 z-10 -mx-5 mt-2 bg-gradient-to-t from-[#F4F6F8] from-85% to-transparent px-5 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-5 sm:mt-4">
            <div className="flex items-baseline justify-between border-t border-[#e7e5e4] pt-3">
              <span className="text-base font-medium">Total</span>
              <span className="text-2xl font-bold tabular-nums tracking-tight sm:text-3xl">${formatPlanPrice(selectedPack.price)}</span>
            </div>
            {error ? (
              <p role="alert" className="mt-3 text-sm text-red-700">
                {error}
              </p>
            ) : null}
            <button
              type="submit"
              disabled={submitting || uploading}
              className={`mt-3 ${primaryButtonClass}`}
              style={primaryButtonStyle}
            >
              {submitting
                ? 'Opening payment…'
                : uploading
                  ? `Finishing upload ${uploadProgress}%`
                  : `Pay $${formatPlanPrice(selectedPack.price)}`}
            </button>
            <p className="mt-2 text-center text-xs text-[#57534e] sm:text-sm">
              Card payment on a secure page using CyberSource.
            </p>
          </div>
          </>
          )}
        </form>
      </main>
    </div>
  )
}

export function QuickUploadDialog({ open, onClose, inHub = false, buyOnly = false }) {
  useEffect(() => {
    if (!open) return undefined
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [open])

  if (!open) return null

  return createPortal(
    <div
      className="fixed inset-0 z-[400] flex items-center justify-center sm:px-4 sm:pb-[max(1rem,env(safe-area-inset-bottom))] sm:pt-[max(1rem,env(safe-area-inset-top))]"
      role="presentation"
    >
      <div aria-hidden className="absolute inset-0 bg-black/75" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="quick-upload-title"
        className="relative flex h-[100dvh] w-full max-w-lg flex-col overflow-hidden bg-[#F4F6F8] text-[#1c1917] shadow-[0_28px_70px_-24px_rgba(0,0,0,0.65)] transition-[max-width] duration-300 ease-out sm:h-auto sm:max-h-[min(920px,calc(100dvh-2rem))] sm:rounded-[28px] lg:has-[[data-wide-step]]:max-w-[1000px]"
      >
        <QuickUpload onClose={onClose} inHub={inHub} buyOnly={buyOnly} />
      </div>
    </div>,
    document.body,
  )
}
